import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import {
    Box,
    Card,
    Typography,
    Grid,
    Avatar,
    Button,
    styled,
    useTheme,
    useMediaQuery,
    Fab,
    Zoom,
    Tooltip,
    Stack,
} from "@mui/material";

import { AiFillBulb, AiOutlineRead } from "react-icons/ai";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CodeIcon from "@mui/icons-material/Code";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";

/* ✅ Assets */
const bgImage = "https://images.unsplash.com/photo-1499951360447-b19be8fe80f5";

/* ✅ Styled Components - Clean Code Principles */

const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundImage: `linear-gradient(rgba(16,7,32,0.9), rgba(16,7,32,0.95)), url(${bgImage})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    paddingBottom: "80px",
    paddingTop: theme.spacing(12),
    color: "white",
    [theme.breakpoints.down("sm")]: {
        paddingTop: theme.spacing(8),
    },
}));

const SectionCard = styled(Card)(({ theme }) => ({
    backgroundColor: "rgba(16,7,32,0.85)",
    borderRadius: "14px",
    padding: theme.spacing(3),
    marginBottom: "25px",
    boxShadow: "0 4px 14px rgba(0,0,0,0.25)",
    border: "1px solid rgba(255,255,255,0.1)",
    color: "white",
}));

const CodeContainer = styled(Box)(({ theme }) => ({
    position: "relative",
    marginTop: theme.spacing(2),
}));

const CodeBlock = styled("pre")(({ theme }) => ({
    backgroundColor: "#0d1117",
    color: "#c9d1d9",
    padding: "20px",
    borderRadius: "10px",
    overflowX: "auto",
    fontSize: "0.9rem",
    lineHeight: 1.6,
    border: "1px solid rgba(255,255,255,0.05)",
    fontFamily: "'Fira Code', 'Courier New', monospace",
}));

const ActionButton = styled(Button)(({ theme }) => ({
    backgroundColor: "goldenrod",
    color: "#100720",
    fontWeight: 800,
    padding: "12px 40px",
    borderRadius: "999px",
    transition: "0.3s",
    "&:hover": {
        backgroundColor: "#e0b200",
        transform: "scale(1.05)",
    },
}));

/* ✅ Main Component */
export default function JavaCollections() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [showScroll, setShowScroll] = useState(false);
    const [copiedIndex, setCopiedIndex] = useState(null);

    useEffect(() => {
        const checkScroll = () => setShowScroll(window.pageYOffset > 400);
        window.addEventListener("scroll", checkScroll);
        return () => window.removeEventListener("scroll", checkScroll);
    }, []);

    const handleScrollTop = () => {
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const handleCopy = (text, index) => {
        navigator.clipboard.writeText(text);
        setCopiedIndex(index);
        setTimeout(() => setCopiedIndex(null), 2000);
    };

    const collectionModules = useMemo(() => [
        {
            title: "1. The List Interface (ArrayList)",
            code: `import java.util.ArrayList;\n\npublic class Main {\n  public static void main(String[] args) {\n    ArrayList<String> fruits = new ArrayList<>();\n    fruits.add("Apple");\n    fruits.add("Banana");\n    fruits.add("Cherry");\n\n    System.out.println(fruits); // Output: [Apple, Banana, Cherry]\n  }\n}`,
            desc: "An ArrayList is a dynamic array that grows as needed. It maintains insertion order and allows duplicate elements. It is the go-to choice when frequent data retrieval by index is required.",
            diagram: ""
        },
        {
            title: "2. The Set Interface (HashSet)",
            code: `import java.util.HashSet;\n\npublic class Main {\n  public static void main(String[] args) {\n    HashSet<Integer> numbers = new HashSet<>();\n    numbers.add(10);\n    numbers.add(20);\n    numbers.add(10); // Duplicate ignored\n\n    System.out.println(numbers); // Output: [10, 20]\n  }\n}`,
            desc: "A Set is a collection that contains no duplicate elements. HashSet uses a hash table for storage, making it highly efficient for checking if an item exists within the collection.",
            diagram: ""
        },
        {
            title: "3. The Map Interface (HashMap)",
            code: `import java.util.HashMap;\n\npublic class Main {\n  public static void main(String[] args) {\n    HashMap<String, Integer> scores = new HashMap<>();\n    scores.put("Alice", 90);\n    scores.put("Bob", 85);\n\n    System.out.println("Alice's Score: " + scores.get("Alice"));\n  }\n}`,
            desc: "Maps store data in 'Key-Value' pairs. Each key is unique and maps to exactly one value. HashMap is ideal for high-performance data lookups based on a specific identifier.",
            diagram: ""
        },
        {
            title: "4. The Queue Interface (LinkedList)",
            code: `import java.util.LinkedList;\nimport java.util.Queue;\n\npublic class Main {\n  public static void main(String[] args) {\n    Queue<String> queue = new LinkedList<>();\n    queue.add("Task1");\n    queue.add("Task2");\n\n    System.out.println("Next up: " + queue.poll()); // Removes Task1\n  }\n}`,
            desc: "Queues are designed for holding elements prior to processing, typically in a First-In-First-Out (FIFO) order. LinkedList implements Queue, providing efficient insertion and deletion.",
            diagram: ""
        }
    ], []);

    return (
        <PageWrapper>
            {/* ✅ Page Header */}
            <Box sx={{ textAlign: "center", mb: 6, px: 2 }}>
                <Typography
                    variant={isMobile ? "h5" : "h3"}
                    sx={{
                        fontWeight: 800,
                        color: "goldenrod",
                        display: "inline-block",
                        px: { xs: 3, md: 6 },
                        py: 1.5,
                        borderRadius: "999px",
                        boxShadow: "0 6px 20px rgba(218, 165, 32, 0.3)",
                    }}
                >
                    JAVA COLLECTIONS FRAMEWORK
                </Typography>
            </Box>

            <Grid container spacing={4} sx={{ px: { xs: 2, md: 6 } }}>
                {/* 🎯 Tutorial Introduction & Coverage */}
                <Grid item xs={12}>
                    <SectionCard>
                        <Typography variant="h5" fontWeight={800} color="goldenrod" mb={2}>
                            Tutorial Overview
                        </Typography>
                        <Typography variant="body1" sx={{ opacity: 0.9, mb: 3 }}>
                            Mastering the <strong>Collections Framework</strong> is a turning point for any Java developer. Instead of manually managing arrays, you will learn to use sophisticated data structures that optimize memory and performance. In this module, we are covering:
                        </Typography>
                        <Grid container spacing={2} sx={{ mb: 4 }}>
                            {[
                                "Dynamic Data Storage (Lists)",
                                "Ensuring Uniqueness (Sets)",
                                "Key-Value Relationship (Maps)",
                                "Processing Order (Queues)",
                                "Choosing the Right Data Structure"
                            ].map((item, index) => (
                                <Grid item xs={12} sm={6} md={4} key={index}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                        <CheckIcon sx={{ color: "goldenrod", fontSize: 20 }} />
                                        <Typography variant="body2">{item}</Typography>
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>

                        <Typography variant="h6" fontWeight={800} color="goldenrod" mb={1}>
                            The Collection Hierarchy
                        </Typography>
                        <Typography mb={3} sx={{ opacity: 0.9 }}>
                            The framework is built on a set of interfaces. By coding to these interfaces, your software becomes more flexible and easier to maintain.
                        </Typography>



                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 4, mb: 2 }}>
                            <Avatar sx={{ bgcolor: "goldenrod", color: "#100720" }}>
                                <AiFillBulb size={24} />
                            </Avatar>
                            <Typography variant="h6" fontWeight={700}>Performance Tips</Typography>
                        </Box>
                        <Typography variant="body2" sx={{ opacity: 0.8, pl: 1, lineHeight: 1.8 }}>
                            • <strong>ArrayList:</strong> Best for random access ($O(1)$ time complexity).<br />
                            • <strong>LinkedList:</strong> Superior for frequent additions/removals ($O(1)$ at ends).<br />
                            • <strong>HashMap:</strong> Provides near-instant retrieval using hashing ($O(1)$ average).
                        </Typography>
                    </SectionCard>
                </Grid>

                {/* ✅ Learning Modules */}
                {collectionModules.map((item, i) => (
                    <Grid item xs={12} key={i}>
                        <SectionCard>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, flexWrap: "wrap", gap: 2 }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                    <Avatar sx={{ bgcolor: "rgba(218, 165, 32, 0.2)", color: "goldenrod" }}>
                                        <AiOutlineRead size={24} />
                                    </Avatar>
                                    <Typography variant="h6" fontWeight={700}>
                                        {item.title}
                                    </Typography>
                                </Box>

                                <Stack direction="row" spacing={1}>
                                    <Tooltip title={copiedIndex === i ? "Copied!" : "Copy Code"}>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => handleCopy(item.code, i)}
                                            sx={{
                                                color: "white",
                                                borderColor: "rgba(255,255,255,0.3)",
                                                "&:hover": { borderColor: "white" }
                                            }}
                                        >
                                            {copiedIndex === i ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                                        </Button>
                                    </Tooltip>
                                    <Link to="/compiler" style={{ textDecoration: "none" }}>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            startIcon={<CodeIcon />}
                                            sx={{
                                                color: "goldenrod",
                                                borderColor: "goldenrod",
                                                "&:hover": { borderColor: "#e0b200", bgcolor: "rgba(218, 165, 32, 0.1)" }
                                            }}
                                        >
                                            Try it
                                        </Button>
                                    </Link>
                                </Stack>
                            </Box>

                            <Typography variant="body1" sx={{ mb: 3, opacity: 0.9, lineHeight: 1.6 }}>
                                {item.desc}
                            </Typography>

                            <Box sx={{ textAlign: "center", my: 2 }}>
                                {item.diagram}
                            </Box>

                            <CodeContainer>
                                <CodeBlock>{item.code}</CodeBlock>
                            </CodeContainer>
                        </SectionCard>
                    </Grid>
                ))}

                {/* ✅ Navigation Footer */}
                <Grid item xs={12} textAlign="center" sx={{ mt: 4 }}>
                    <Link to="/javadashboard" style={{ textDecoration: "none" }}>
                        <ActionButton variant="contained">
                            BACK TO DASHBOARD
                        </ActionButton>
                    </Link>
                </Grid>
            </Grid>

            {/* ✅ Floating Back to Top Button */}
            <Zoom in={showScroll}>
                <Fab
                    size="small"
                    onClick={handleScrollTop}
                    sx={{ position: "fixed", bottom: 25, right: 25, bgcolor: "goldenrod", "&:hover": { bgcolor: "#e0b200" } }}
                >
                    <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                </Fab>
            </Zoom>
        </PageWrapper>
    );
}
